import React, { useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Menu as MenuIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { USER_ROLES, ROUTES_PATHS, PROJECT_TYPES } from '../../shared/appConstants';
import { ICONS } from '../../shared/icons';
import { toggleSidebar } from '../../store/uiSlice';
import { logout } from '../../store/authSlice';
import logoutIcon from '../../assets/images/logout.svg';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { isSidebarCollapsed } = useSelector((state) => state.ui);
  const { filters, projectDropdownsByUser } = useSelector((state) => state.tickets);
  const { t } = useTranslation();

  const selectedProjectId = filters?.selectedProject;
  const selectedProject = projectDropdownsByUser?.find(p => String(p.id) === String(selectedProjectId));
  const lastSelectedView = localStorage.getItem('lastSelectedView') || '/ticket-management';
  
  // Is this route strictly tied to Scrum?
  const isStrictScrumRoute = location.pathname === ROUTES_PATHS.BACKLOG_MANAGEMENT || location.pathname === ROUTES_PATHS.SPRINT_TIMELINE || location.pathname === ROUTES_PATHS.SPRINT_MANAGEMENT;
  
  // Is this route strictly tied to Kanban?
  const isStrictKanbanRoute = location.pathname === '/ticket-management' || location.pathname === '/dashboard' || location.pathname === '/timesheet' || location.pathname === ROUTES_PATHS.STRATEGIST_AGENT;

  // Inherit context for shared routes like /settings, /user-management, /space-management, etc.
  const isScrumContext = isStrictScrumRoute || (!isStrictKanbanRoute && lastSelectedView === ROUTES_PATHS.BACKLOG_MANAGEMENT);
  const isKanbanContext = isStrictKanbanRoute || (!isStrictScrumRoute && lastSelectedView === '/ticket-management');
  
  const isScrumProject = selectedProject?.project_type?.toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase() || (!selectedProject && isScrumContext);
  const isKanbanProject = selectedProject?.project_type?.toUpperCase() === PROJECT_TYPES.KANBAN.toUpperCase() || (!selectedProject && isKanbanContext);

  // Handle body class for layout adjustment
  useEffect(() => {
    if (isSidebarCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  }, [isSidebarCollapsed]);

  // Track last selected view
  useEffect(() => {
    if (location.pathname === '/ticket-management' || location.pathname === ROUTES_PATHS.BACKLOG_MANAGEMENT || location.pathname === ROUTES_PATHS.SPRINT_TIMELINE || location.pathname === ROUTES_PATHS.SPRINT_MANAGEMENT) {
      localStorage.setItem('lastSelectedView', location.pathname);
    }
  }, [location.pathname]);

  // Centralized route matching logic with memoization
  const isActive = useMemo(() => {
    const currentPath = location.pathname;
    return (path) => {
      if (path === '/settings') {
        return currentPath === path
          || currentPath.startsWith('/prompt-templates')
          || currentPath.startsWith('/project-alerts')
          || currentPath.startsWith('/notification-settings')
          || currentPath.startsWith('/sprint-settings');
      }
      if (path === '/ticket-management') {
        return currentPath === path || currentPath.startsWith('/notifications');
      }
      return currentPath === path;
    };
  }, [location.pathname]);

  const canAccessAdministration = () => {
    return user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;
  };

  const canAccessUserManagement = () => {
    return user?.role === USER_ROLES.ADMIN;
  };

  const canAccessSpaceManagement = () => {
    return user?.role === USER_ROLES.ADMIN;
  };

  const handleToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const NavItem = ({ path, iconSrc, label, onClick }) => {
    const active = isActive(path);
    const handleActivate = onClick || (() => navigate(path));
    const content = (
      <div
        className={`nav-item ${active ? 'active' : ''}`}
        onClick={handleActivate}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleActivate();
          }
        }}
      >
        {iconSrc && (
          <img
            src={iconSrc}
            alt={label}
            style={{
              width: 20,
              height: 20,
              marginRight: isSidebarCollapsed ? 0 : 12,
              objectFit: 'contain',
            }}
          />
        )}
        {!isSidebarCollapsed && <span>{label}</span>}
      </div>
    );

    if (isSidebarCollapsed) {
      return (
        <Tooltip title={label} placement="right" arrow>
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-branding">
          <MenuIcon className="sidebar-menu-icon" />
          {!isSidebarCollapsed && <span className="sidebar-menu-text">Menu</span>}
        </div>
        <div className="sidebar-toggle-container">
          <IconButton
            onClick={handleToggle}
            className="sidebar-toggle-btn"
            size="small"
          >
            {isSidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </div>
      </div>

      <nav>
        <div className="nav-section">
          {!isSidebarCollapsed && <div className="nav-title">{t('navbar.main')}</div>}

          <NavItem
            path="/ticket-management"
            iconSrc={ICONS.ticketManagement}
            label={t('navbar.ticketManagement')}
          />

          {!isKanbanProject && (
            <>
              <NavItem
                path={ROUTES_PATHS.BACKLOG_MANAGEMENT}
                iconSrc={ICONS.backlogManagement}
                label={t('navbar.backlogManagement')}
              />
              <NavItem
                path={ROUTES_PATHS.SPRINT_TIMELINE}
                iconSrc={ICONS.sprintTimeline}
                label={t('navbar.sprintTimeline')}
              />
              <NavItem
                path={ROUTES_PATHS.SPRINT_MANAGEMENT}
                iconSrc={ICONS.sprintManagement}
                label={t('sprintManagement.title')}
              />
            </>
          )}

           

          {!isScrumProject && (
            <NavItem
              path="/dashboard"
              iconSrc={ICONS.reportsManagement}
              label={t('navbar.reportsDashboard')}
            />
          )}

          <NavItem
            path={ROUTES_PATHS.DAILY_STANDUP}
            iconSrc={ICONS.dailyStandup}
            label={t('navbar.dailyStandup')}
          />

          <NavItem
            path="/timesheet"
            iconSrc={ICONS.timesheet}
            label={t('navbar.timesheet')}
          />

          <NavItem
            path="/settings"
            iconSrc={ICONS.setting}
            label={t('navbar.settings')}
          />

          {!canAccessAdministration() && (
            <NavItem
              path="/project-management"
              iconSrc={ICONS.projectManagement}
              label={t('navbar.projectManagement')}
            />
          )}
        </div>

        {canAccessAdministration() && (
          <div className="nav-section">
            {!isSidebarCollapsed && <div className="nav-title">{t('navbar.administration')}</div>}

            {canAccessUserManagement() && (
              <NavItem
                path="/user-management"
                iconSrc={ICONS.userManagement}
                label={t('navbar.userManagement')}
              />
            )}

            {canAccessSpaceManagement() && (
              <NavItem
                path="/space-management"
                iconSrc={ICONS.spaceManagement}
                label={t('navbar.spacesManagement')}
              />
            )}

            <NavItem
              path="/project-management"
              iconSrc={ICONS.projectManagement}
              label={t('navbar.projectManagement')}
            />

            {canAccessAdministration() && !isScrumProject && (
              <NavItem
                path={ROUTES_PATHS.STRATEGIST_AGENT}
                iconSrc={ICONS.strategistAgent}
                label={t('navbar.strategistAgent')}
              />
            )}

            {/* Permissions sidebar item - commented for future implementation. */}
            {/* {canAccessUserManagement() && (
              <NavItem
                path="/permissions"
                iconSrc={ICONS.permission}
                label={t('navbar.permissions')}
              />
            )} */}
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        <NavItem
          path="/login"
          iconSrc={logoutIcon}
          label={t('navigation.logout')}
          onClick={handleLogout}
        />
      </div>
    </aside>
  );
};

export default Navbar;
